import { useState } from 'react'
import Checkout from '@/components/Checkout'
import OrderPlaced from '@/components/OrderPlaced'
import { MenuSource, OperationalMenu } from '@/libs/helpers/adapters'
import { GiftCard, Order, PaymentMethod, getOnlineOperationalMenu } from '@/libs/helpers/apiClient'
import { ClientErrorStatusCode, parseRequestError } from '@/libs/helpers/error_handler'
import { OrderMethod } from '@/libs/helpers/utils'
import { SSRPage, withServerSideProps } from '@/libs/helpers/withServerSidePropsDecorator'

type CheckoutPageProps = {
  giftCards: GiftCard[]
  paymentMethods: PaymentMethod[]
  venueCountry: string
  menuSource: MenuSource
  operationalMenu: OperationalMenu
}

export const getServerSideProps = withServerSideProps<CheckoutPageProps>({
  onPageRequest: async ({ params }, { headers, initialCart, venue, guest, user }) => {
    const { orderMethod: paramOrderMethod, venueXRefID } = params
    const orderMethod = OrderMethod[paramOrderMethod]

    const checkoutProps: CheckoutPageProps = {
      giftCards: [],
      paymentMethods: [],
      venueCountry: '',
      menuSource: 'RMM2',
      operationalMenu: {
        menuPages: {},
        menuGroups: {},
        menuItems: {},
        modifierGroups: {},
        modifierOptions: {},
      },
    }

    // MENU
    try {
      const menu = await getOnlineOperationalMenu(venueXRefID, {
        headers,
        params: {
          orderMethod,
          ...(initialCart?.scheduledFor != null ? { orderTime: initialCart.scheduledFor } : {}),
        },
      })

      checkoutProps.menuSource = menu.source
      checkoutProps.operationalMenu = {
        menuPages: menu.refs.menuPages,
        menuGroups: menu.refs.menuGroups,
        menuItems: menu.refs.menuItems,
        modifierGroups: menu.refs.modifierGroups,
        modifierOptions: menu.refs.modifierOptions,
      }
    } catch (error) {
      if (parseRequestError(error).statusCode !== ClientErrorStatusCode.NOT_FOUND) {
        throw error
      }
    }

    if (venue?.guestCheckoutEnabled && !guest && !user) {
      return {
        redirect: {
          permanent: false,
          destination: `/${paramOrderMethod}/${venueXRefID}/menu`,
        },
      }
    }

    return {
      props: checkoutProps,
    }
  },
})

const CheckoutPage: SSRPage<CheckoutPageProps> = (checkoutPageProps) => {
  const [processedGuestOrder, setProcessedGuestOrder] = useState<Order | null>(null)

  return processedGuestOrder ? (
    <OrderPlaced {...checkoutPageProps} order={processedGuestOrder} />
  ) : (
    <Checkout {...checkoutPageProps} setProcessedGuestOrder={setProcessedGuestOrder} />
  )
}

export default CheckoutPage
